import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/observable';
import { tap } from 'rxjs/operators';
import { AppConfigsService } from '../../../config/app-configs.service';
import {
    HttpService,
    IEmptyResult,
    IResponseLower
} from '../../../shared/cardholders-core/services/http-service.service';
import { ISavingContent } from '../interfaces/content.interface';
import { ISavingCard, ISavingEligibilityResponse, UserInfo } from '../interfaces/ISavingEligibilityResponse.interface';
import { ErrorType, SuccessType } from '../interfaces/saving.interface';
import { IChooseOptionRequest, IChooseOptionResponse } from '../interfaces/IChooseOption.interface';
import { IUploadFile } from '../interfaces/ISavingFileRequest.interface';
import { IConfirmSideMoneyResponse } from '../interfaces/IConfirmSideMoney.interface';
import { IExternalSavingData } from '../interfaces/IExternalSavingData.interface';
import { IChoosePolicyRequest, KnowBetterSide, MaxNewPolicyInput } from '../interfaces/IChoosePolicy.interface';
import { UiHelperService } from '../../../shared/cardholders-core/services/ui-helper.service';
import { AuthService } from '../../../shared/cardholders-core/services/auth.service';
import { ISelectedMaslulim } from '../interfaces/IMonyPlusSuccessTable.interface';

@Injectable()
export class SavingService {
    externalSavingData: IExternalSavingData = { investCode: 5 };
    showInsuranceLoader = new BehaviorSubject<boolean>(false);
    refId: any;
    isSSO = false;
    savingEligibility: { displaySide: boolean; displayExternal: boolean };
    get loaderStatus(): Observable<boolean> {
        return this._loaderStatus.asObservable();
    }
    set loader(add: boolean) {
        if (add) {
            this.loaderCounter += 1;
            this._loaderStatus.next(true);
        } else {
            this.loaderCounter -= 1;
            if (this.loaderCounter < 0) {
                this.loaderCounter = 0;
            }
            if (this.loaderCounter === 0) {
                this._loaderStatus.next(false);
            }
        }
    }
    baseApi: string;
    savingEligibilityResponse: ISavingEligibilityResponse;
    content = new BehaviorSubject<ISavingContent>(null);
    next = '';
    errorType: ErrorType;
    successType: SuccessType;
    chooseOptionRequest: IChooseOptionRequest;
    savingFileReq: IUploadFile;
    displayIssueDatePhoto: boolean;
    chooseOptionResponse: IChooseOptionResponse;
    chooseCard: ISavingCard;
    disableLinkLayout = new BehaviorSubject<boolean>(false);
    shouldUpdateApp: boolean;
    isDirectLink: boolean;
    isPassKnowBetterSideSaving = true;
    private _loaderStatus = new BehaviorSubject<boolean>(false);
    private loaderCounter = 0;
    selectedMaslulim: ISelectedMaslulim[];
    constructor(
        private httpSvc: HttpService,
        private appConfigsService: AppConfigsService,
        public uiHelper: UiHelperService,
        private authService: AuthService
    ) {
        this.baseApi = `${this.appConfigsService.appConfigs.apiUrl}/registered/saving`;
        this.getContent().toPromise();
    }

    getUserValid(): Observable<IResponseLower<UserInfo>> {
        this.loader = true;
        return this.httpSvc.getLower<UserInfo>(`${this.baseApi}/userInfo`).pipe(
            tap(x => {
                this.loader = false;
            })
        );
    }
    getContent(): Observable<IResponseLower<ISavingContent>> {
        this.loader = true;
        return this.httpSvc.getLower<ISavingContent>(`${this.baseApi}/getContent`).pipe(
            tap(x => {
                this.loader = false;
                if (x.returnCode === 0) {
                    this.content.next(x.result);
                }
            })
        );
    }
    checkEligibility(showLoader: boolean = true): Observable<IResponseLower<ISavingEligibilityResponse>> {
        if (showLoader) {
            this.loader = true;
        }

        return this.httpSvc.postLower<ISavingEligibilityResponse>(`${this.baseApi}/checkEligibility`, null).pipe(
            tap(x => {
                if (showLoader) {
                    this.loader = false;
                }

                this.savingEligibilityResponse = x.result;
            })
        );
    }
    confirmSideMoney(): Observable<IResponseLower<IConfirmSideMoneyResponse>> {
        this.loader = true;
        return this.httpSvc.postLower<IConfirmSideMoneyResponse>(`${this.baseApi}/confirmSideMoney`, null).pipe(
            tap(x => {
                this.shouldUpdateApp = x.result?.shouldUpdateApp;
                this.loader = false;
            })
        );
    }

    uploadFiles(upload: any): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        this.savingFileReq = upload;
        return this.httpSvc.postLower(`${this.baseApi}/saveFile`, upload).pipe(
            tap(x => {
                this.loader = false;
            })
        );
    }
    uploadFront(upload: string): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        return this.httpSvc.postLower(`${this.baseApi}/uploadFront`, { front: upload }).pipe(
            tap(x => {
                this.loader = false;
            })
        );
    }
    openLead(): Observable<IResponseLower<number>> {
        this.loader = true;
        return this.httpSvc
            .postLower<number>(`${this.baseApi}/createLead`, { RefID: this.refId })
            .pipe(
                tap(x => {
                    this.loader = false;
                })
            );
    }

    chooseOption(
        chooseOptionRequest: IChooseOptionRequest,
        savingCard: ISavingCard
    ): Observable<IResponseLower<IChooseOptionResponse>> {
        this.loader = true;
        this.chooseOptionRequest = chooseOptionRequest;
        this.chooseCard = savingCard;
        return this.httpSvc.postLower<IChooseOptionResponse>(`${this.baseApi}/chooseOption`, chooseOptionRequest).pipe(
            tap(x => {
                this.loader = false;
                if (x.returnCode === 0) {
                    this.chooseOptionResponse = x.result;
                }
            })
        );
    }
    choosePolicy(choosePolicyRequest: IChoosePolicyRequest[]): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        return this.httpSvc
            .postLower<Observable<IResponseLower<IEmptyResult>>>(`${this.baseApi}/ChoosePolicy`, choosePolicyRequest)
            .pipe(
                tap(x => {
                    this.loader = false;
                })
            );
    }
    saveFinishLevel(choosePolicyRequest: any): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        return this.httpSvc
            .postLower<Observable<IResponseLower<IEmptyResult>>>(`${this.baseApi}/saveFinishLevel`, choosePolicyRequest)
            .pipe(
                tap(x => {
                    this.loader = false;
                })
            );
    }
    deleteSession(): void {
        if (this.uiHelper.isBrowser && this.authService.isUserAuthenticated()) {
            this.httpSvc
                .getLower<Observable<IResponseLower<IEmptyResult>>>(`${this.baseApi}/removeSessionState`)
                .toPromise();
        }
    }

    saveKnowBetter(maxNewPolicyInput: MaxNewPolicyInput): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        this.externalSavingData.knowBetter = maxNewPolicyInput;
        return this.httpSvc
            .postLower<Observable<IResponseLower<IEmptyResult>>>(`${this.baseApi}/saveKnowBetter`, maxNewPolicyInput)
            .pipe(
                tap(x => {
                    this.loader = false;
                    if (x.returnCode === 0) {
                    }
                })
            );
    }
    saveKnowBetterSide(knowBetterSide: KnowBetterSide): Observable<IResponseLower<IEmptyResult>> {
        this.loader = true;
        this.externalSavingData.knowBetterSide = knowBetterSide;
        return this.httpSvc
            .postLower<Observable<IResponseLower<IEmptyResult>>>(`${this.baseApi}/saveKnowBetterSide`, knowBetterSide)
            .pipe(
                tap(x => {
                    this.loader = false;
                })
            );
    }

    getSelectedMaslulim(): void {
        this.selectedMaslulim = this.externalSavingData.pitsul
            ? Object.entries(this.externalSavingData.pitsul)
                  .filter(([key, value]) => value > 0)
                  .map(([key, value]) => {
                      switch (key) {
                          case 'global':
                              return {
                                  maslul: 'כללי',
                                  precentage: this.appConfigsService.appConfigs.GlobalMaslulAnnualCostPercentage
                              };
                          case 'stock':
                              return {
                                  maslul: 'מניות',
                                  precentage: this.appConfigsService.appConfigs.StockMaslulAnnualCostPercentage
                              };
                          case 'obligation':
                              return {
                                  maslul: 'אשראי ואג"ח',
                                  precentage: this.appConfigsService.appConfigs.ObligationMaslulAnnualCostPercentage
                              };
                          case 'israelObligation':
                              return {
                                  maslul: 'אג"ח ממשלות',
                                  precentage: this.appConfigsService.appConfigs
                                      .IsraelObligationMaslulAnnualCostPercentage
                              };
                      }
                  })
            : [{ maslul: 'כללי', precentage: this.appConfigsService.appConfigs.GlobalMaslulAnnualCostPercentage }];
    }
}
